'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Menu, X } from 'lucide-react';
import Image from 'next/image';
import { useAuth } from '@/contexts/AuthContext';

const navLinks = [
  { label: 'Programmes', href: '/course-marketplace' },
  { label: 'Advisory Council', href: '/advisory-council' },
  { label: 'Corporate Training', href: '/corporate-training' },
  { label: 'Governance', href: '/governance' },
  { label: 'About', href: '/#why-reusa' },
  { label: 'Contact', href: '/#contact' },
];

export default function LandingNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();

  const studentPortalHref = user ? '/student-dashboard' : '/login';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-white/97 backdrop-blur-md shadow-sm border-b border-border'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-screen-xl mx-auto px-8 lg:px-14 xl:px-20">
          <div className="flex items-center justify-between h-[72px]">

            {/* Logo — links to homepage */}
            <Link href="/" className="flex items-center gap-2 flex-shrink-0" aria-label="REUSA Home">
              <Image
                src="/assets/images/REUSA_PNG_LOGO__4_-1781810452969.png"
                alt="REUSA logo"
                width={108}
                height={38}
                className="object-contain transition-all duration-300"
                priority
              />
            </Link>

            {/* Desktop nav — centered */}
            <div className="hidden lg:flex items-center gap-0">
              {navLinks?.map((link) => (
                <Link
                  key={`nav-${link?.label}`}
                  href={link?.href}
                  className={`font-sans text-[0.7rem] font-medium px-3 py-2 tracking-wider uppercase transition-all duration-200 whitespace-nowrap ${
                    scrolled
                      ? 'text-foreground/60 hover:text-primary'
                      : 'text-white/60 hover:text-white'
                  }`}
                  style={{ letterSpacing: '0.06em' }}
                >
                  {link?.label}
                </Link>
              ))}
            </div>

            {/* Student Portal CTA */}
            <div className="hidden lg:flex items-center gap-5">
              <Link
                href={studentPortalHref}
                className={`font-sans text-[0.8125rem] font-semibold tracking-widest uppercase transition-all duration-200 px-5 py-2 ${
                  scrolled
                    ? 'text-primary hover:text-primary/70' :'text-white/80 hover:text-white'
                }`}
                style={{ letterSpacing: '0.1em' }}
              >
                Student Portal
              </Link>
              <Link
                href="/apply"
                className="btn-gold text-[0.75rem] px-6 py-2.5"
              >
                Apply Now
              </Link>
            </div>

            {/* Mobile hamburger */}
            <button
              className={`lg:hidden p-2 rounded-lg transition-colors ${
                scrolled ? 'text-primary hover:bg-secondary' : 'text-white hover:bg-white/10'
              }`}
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle mobile menu"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div
          className="absolute inset-0 bg-foreground/40 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        />
        <div
          className={`absolute top-0 right-0 bottom-0 w-72 bg-white shadow-2xl transition-transform duration-300 ${
            mobileOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex items-center justify-between px-6 py-5 border-b border-border">
            <span className="font-serif font-bold text-base text-primary tracking-wide">REUSA</span>
            <button
              onClick={() => setMobileOpen(false)}
              className="p-2 rounded-lg hover:bg-secondary text-muted-foreground"
              aria-label="Close menu"
            >
              <X size={18} />
            </button>
          </div>
          <div className="p-5 space-y-1">
            {navLinks?.map((link) => (
              <Link
                key={`mobile-nav-${link?.label}`}
                href={link?.href}
                className="block px-4 py-3 font-sans text-xs font-semibold text-foreground/70 hover:text-primary tracking-widest uppercase transition-colors"
                style={{ letterSpacing: '0.1em' }}
                onClick={() => setMobileOpen(false)}
              >
                {link?.label}
              </Link>
            ))}
          </div>
          <div className="px-5 pb-5 space-y-3 border-t border-border pt-5">
            <Link
              href={studentPortalHref}
              className="block w-full text-center px-4 py-3 font-sans text-xs font-semibold text-primary border border-primary tracking-widest uppercase transition-all hover:bg-primary hover:text-white"
              style={{ letterSpacing: '0.1em' }}
              onClick={() => setMobileOpen(false)}
            >
              Student Portal
            </Link>
            <Link
              href="/apply"
              className="btn-gold block w-full text-center px-4 py-3 text-xs"
              onClick={() => setMobileOpen(false)}
            >
              Apply Now
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}